import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { captureEvent } from '../lib/analytics';

/**
 * Social sign-in has no success handler — the browser leaves the origin and
 * returns via a redirect issued by the auth server. Everything the email form's
 * success handler used to do therefore happens here instead, on return.
 *
 * Mounted once, globally, because the user can land on any route.
 */
export function AuthReturnHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const marker = params.get('auth');
    if (marker !== 'google' && marker !== 'google-new') return;

    captureEvent(marker === 'google-new' ? 'user_signed_up' : 'user_signed_in', {
      method: 'google',
    });

    // Clearing this is deliberate for both markers, but the reason differs:
    // for 'google' the saved value was consumed into the callback URL, so
    // clearing is just cleanup. For 'google-new' it was NEVER consumed —
    // Better Auth routes new users via newUserCallbackURL, a fixed
    // '/onboarding', ignoring the saved return URL entirely. Either way,
    // leaving it behind would make HomeRedirect re-fire a stale redirect on
    // a later visit to '/', so clearing is still correct — it just also
    // disables that safety net for new users landing on /onboarding.
    //
    // This is harmless today only because the two values happen to coincide:
    // FitResultsPage.tsx always saves '/video-upload', and
    // OnboardingPage.tsx:144 always hard-navigates there on completion. If
    // either changes independently, new Google users will silently lose
    // their mid-funnel destination.
    sessionStorage.removeItem('bikendure_return_url');

    // `/` is not a page — HomeRedirect is already leaving it, so there is no URL
    // left to tidy. Navigating anyway would land in the same commit as
    // HomeRedirect's own redirect and, holding a stale `location`, bounce the
    // user straight back to `/` — overwriting the destination it just resolved.
    if (location.pathname === '/') return;

    params.delete('auth');
    const query = params.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}${location.hash}`, {
      replace: true,
    });
  }, [location, navigate]);

  return null;
}
